import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPoster, updatePoster } from '../../Store/posters/postersSlice';
import { getProducts } from '../../Store/products/productsSlice';
import ProductSelectModal from './ProductSelectModal';
import styles from '../../styles/AdminPanel.module.scss';
import { posterUrl } from '../../utils/imageUrl';

const PosterForm = ({ poster, onCancel, onSuccess, onDelete, showMessage }) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.posters);
  const { list: products } = useSelector((state) => state.products);
  const isEdit = Boolean(poster?.id);

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [linkMode, setLinkMode] = useState('product');

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    heading: '',
    buttonText: 'Купити зараз',
    image: null,
    imagePreview: '',
    productId: '',
    productTitle: '',
    buttonLink: '/',
    sortOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    dispatch(getProducts());
  }, [dispatch]);

  useEffect(() => {
    if (poster) {
      const hasProduct = Boolean(poster.productId);
      setLinkMode(hasProduct ? 'product' : 'link');
      setFormData({
        title: poster.title || '',
        subtitle: poster.subtitle || '',
        heading: poster.heading || '',
        buttonText: poster.buttonText || 'Купити зараз',
        image: null,
        imagePreview: poster.image ? posterUrl(poster.image) : '',
        productId: poster.productId || '',
        productTitle: '',
        buttonLink: poster.buttonLink || '/',
        sortOrder: poster.sortOrder ?? 0,
        isActive: poster.isActive !== 0,
      });
    }
  }, [poster]);

  useEffect(() => {
    if (formData.productId && products.length) {
      const product = products.find((p) => p.id === Number(formData.productId));
      if (product) {
        setFormData((prev) => ({ ...prev, productTitle: product.title }));
      }
    }
  }, [formData.productId, products]);

  const previewLink = useMemo(() => {
    if (linkMode === 'product' && formData.productId) {
      return `/products/${formData.productId}`;
    }
    return formData.buttonLink || '/';
  }, [linkMode, formData.productId, formData.buttonLink]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'Розмір зображення не повинен перевищувати 5 МБ');
      return;
    }
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Оберіть файл зображення');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      image: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imagePreview: isEdit && poster?.image ? posterUrl(poster.image) : '',
    }));
  };

  const handleProductSelect = (selectedProduct) => {
    setFormData((prev) => ({
      ...prev,
      productId: selectedProduct.id,
      productTitle: selectedProduct.title,
      heading: prev.heading || selectedProduct.title,
      buttonText: prev.buttonText || 'Купити зараз',
    }));
    setLinkMode('product');
    showMessage('success', "Товар прив'язано до постера");
  };

  const clearProduct = () => {
    setFormData((prev) => ({
      ...prev,
      productId: '',
      productTitle: '',
    }));
  };

  const validate = () => {
    if (!formData.title.trim() || !formData.subtitle.trim() || !formData.heading.trim() || !formData.buttonText.trim()) {
      showMessage('error', "Заповніть усі обов'язкові поля");
      return false;
    }
    if (!isEdit && !formData.image && !formData.imagePreview) {
      showMessage('error', 'Завантажте зображення');
      return false;
    }
    if (linkMode === 'product' && !formData.productId) {
      showMessage('error', 'Оберіть товар або вкажіть посилання');
      return false;
    }
    if (linkMode === 'link' && !formData.buttonLink.trim()) {
      showMessage('error', 'Вкажіть посилання кнопки');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const data = new FormData();
      data.append('title', formData.title.trim());
      data.append('subtitle', formData.subtitle.trim());
      data.append('heading', formData.heading.trim());
      data.append('buttonText', formData.buttonText.trim());
      data.append('productId', linkMode === 'product' && formData.productId ? formData.productId : '');
      data.append('buttonLink', linkMode === 'link' ? formData.buttonLink.trim() : '/');
      data.append('sortOrder', parseInt(formData.sortOrder, 10) || 0);
      data.append('isActive', formData.isActive ? '1' : '0');
      if (formData.image) {
        data.append('image', formData.image);
      }

      if (isEdit) {
        await dispatch(updatePoster({ id: poster.id, formData: data })).unwrap();
        showMessage('success', 'Постер успішно оновлено');
      } else {
        await dispatch(createPoster(data)).unwrap();
        showMessage('success', 'Постер успішно створено');
      }
      onSuccess();
    } catch (error) {
      showMessage('error', error || 'Помилка при збереженні постера');
    }
  };

  return (
    <div className={styles.posterFormPage}>
      <form className={styles.posterFormCard} onSubmit={handleSubmit}>
        <div className={styles.posterFormHeader}>
          <div>
            <h2>{isEdit ? `Редагування постера №${poster.id}` : 'Створення постера'}</h2>
            <span>Банер для каруселі на головній сторінці</span>
          </div>
          <div className={styles.posterFormHeaderActions}>
            {isEdit && onDelete && (
              <button type="button" className={styles.posterFormDeleteBtn} onClick={() => onDelete(poster)}>
                Видалити
              </button>
            )}
            <button type="button" className={styles.posterFormCancelBtn} onClick={onCancel}>
              Скасувати
            </button>
            <button type="submit" className={styles.posterFormSaveBtn} disabled={isLoading}>
              {isLoading ? 'Збереження…' : isEdit ? 'Зберегти' : 'Створити'}
            </button>
          </div>
        </div>

        <div className={styles.posterFormBody}>
          <div className={styles.posterFormMain}>
            <section className={styles.posterFormSection}>
              <div className={styles.posterFormSectionHead}>
                <h3>Текст банера</h3>
              </div>
              <div className={styles.posterFormGrid2}>
                <div className={styles.posterFormField}>
                  <label htmlFor="title">Заголовок (акция) *</label>
                  <input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Велика знижка 20%"
                    required
                  />
                </div>
                <div className={styles.posterFormField}>
                  <label htmlFor="subtitle">Подзаголовок *</label>
                  <input
                    id="subtitle"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleChange}
                    placeholder="Розпродаж"
                    required
                  />
                </div>
              </div>
              <div className={styles.posterFormField}>
                <label htmlFor="heading">Назва товару / основной текст *</label>
                <input
                  id="heading"
                  name="heading"
                  value={formData.heading}
                  onChange={handleChange}
                  placeholder="Стрічкова шліфувальна машина APRO SB1100"
                  required
                />
              </div>
              <div className={styles.posterFormField}>
                <label htmlFor="buttonText">Текст кнопки *</label>
                <input
                  id="buttonText"
                  name="buttonText"
                  value={formData.buttonText}
                  onChange={handleChange}
                  placeholder="Купити зараз"
                  required
                />
              </div>
            </section>

            <section className={styles.posterFormSection}>
              <div className={styles.posterFormSectionHead}>
                <h3>Посилання кнопки</h3>
              </div>
              <div className={styles.posterFormLinkMode}>
                <button
                  type="button"
                  className={linkMode === 'product' ? styles.posterFormLinkModeActive : ''}
                  onClick={() => setLinkMode('product')}
                >
                  Товар з каталогу
                </button>
                <button
                  type="button"
                  className={linkMode === 'link' ? styles.posterFormLinkModeActive : ''}
                  onClick={() => setLinkMode('link')}
                >
                  Довільне посилання
                </button>
              </div>
              {linkMode === 'product' ? (
                <div className={styles.posterFormProductPick}>
                  {formData.productId ? (
                    <div className={styles.posterFormProductSelected}>
                      <strong>{formData.productTitle || `Товар #${formData.productId}`}</strong>
                      <div className={styles.posterFormProductActions}>
                        <button type="button" onClick={() => setIsProductModalOpen(true)}>Змінити</button>
                        <button type="button" onClick={clearProduct}>Відв'язати</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className={styles.posterFormPickBtn} onClick={() => setIsProductModalOpen(true)}>
                      + Обрати товар
                    </button>
                  )}
                </div>
              ) : (
                <div className={styles.posterFormField}>
                  <label htmlFor="buttonLink">URL</label>
                  <input
                    id="buttonLink"
                    name="buttonLink"
                    value={formData.buttonLink}
                    onChange={handleChange}
                    placeholder="/ або /products"
                  />
                </div>
              )}
            </section>

            <section className={styles.posterFormSection}>
              <div className={styles.posterFormSectionHead}>
                <h3>Зображення</h3>
              </div>
              <div className={styles.posterFormImageUpload}>
                <label className={styles.posterFormUploadBtn}>
                  Завантажити зображення
                  <input type="file" accept="image/*" onChange={handleImageChange} />
                </label>
                {formData.imagePreview && (
                  <button type="button" className={styles.posterFormRemoveImage} onClick={handleRemoveImage}>
                    Убрать новое фото
                  </button>
                )}
                <small>PNG із прозорим фоном, до 5 МБ</small>
              </div>
            </section>
          </div>

          <aside className={styles.posterFormSidebar}>
            <div className={styles.posterFormSidebarBlock}>
              <h4>Превʼю</h4>
              <div className={styles.posterFormPreview}>
                {formData.title && <div className={styles.posterFormPreviewTitle}>{formData.title}</div>}
                <div className={styles.posterFormPreviewBody}>
                  <div className={styles.posterFormPreviewText}>
                    {formData.subtitle && (
                      <div className={styles.posterFormPreviewSubtitle}>{formData.subtitle}</div>
                    )}
                    {formData.heading && (
                      <div className={styles.posterFormPreviewHeading}>{formData.heading}</div>
                    )}
                    <span className={styles.posterFormPreviewButton}>{formData.buttonText || 'Кнопка'}</span>
                  </div>
                  <div className={styles.posterFormPreviewImage}>
                    {formData.imagePreview ? (
                      <img src={formData.imagePreview} alt="Превʼю" />
                    ) : (
                      <span>Немає зображення</span>
                    )}
                  </div>
                </div>
                <div className={styles.posterFormPreviewLink}>Посилання: {previewLink}</div>
              </div>
            </div>

            <div className={styles.posterFormSidebarBlock}>
              <h4>Налаштування</h4>
              <div className={styles.posterFormField}>
                <label htmlFor="sortOrder">Порядок у каруселі</label>
                <input
                  id="sortOrder"
                  name="sortOrder"
                  type="number"
                  min="0"
                  value={formData.sortOrder}
                  onChange={handleChange}
                />
                <small>Меньшее число — раньше в карусели</small>
              </div>
              <label className={styles.posterFormCheckbox}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                Показувати на головній сторінці
              </label>
            </div>
          </aside>
        </div>
      </form>

      <ProductSelectModal
        products={products}
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSelect={handleProductSelect}
        title="Оберіть товар для постера"
      />
    </div>
  );
};

export default PosterForm;
